import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getPage } from "../api/storefront";
import BlockRenderer from "../Components/BlockRenderer/BlockRenderer";
import ContextHeroSlider from "../Components/Hero/ContextHeroSlider";
import NotFound from "./NotFound";
import Seo from "../Components/Seo/Seo";
import { useSiteSeo } from "../context/SiteSeoContext";
import { absoluteCanonicalUrl } from "../utils/siteUrl";
import { truncateMetaDescription } from "../utils/seoText";
import { usePrerenderReady } from "../utils/prerenderReady";

const RESERVED_SLUGS = new Set([
  "shop",
  "cart",
  "product",
  "loginSignUp",
  "resetPassword",
  "BlogDetails",
  "terms",
  "about",
  "contact",
  "blog",
  "privacy-policy",
  "cookie-policy",
  "shipping-policy",
  "refund-policy",
  "payment",
  "account",
  "festive-vibes",
  "occasions",
  "sitemap.xml",
  "robots.txt",
  "favicon.ico",
]);

// Slugs that are served by the backend API — redirect the browser there directly.
const API_SERVED_SLUGS = new Set(["sitemap.xml", "robots.txt"]);

export default function PageBySlug() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const siteCtx = useSiteSeo();

  const isApiSlug = Boolean(slug && API_SERVED_SLUGS.has(slug.toLowerCase()));
  const invalidSlug = !slug || RESERVED_SLUGS.has(slug);

  // Redirect sitemap.xml / robots.txt to the backend — must be after hooks.
  useEffect(() => {
    if (isApiSlug) {
      const apiBase = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
      window.location.replace(`${apiBase}/${slug}`);
    }
  }, [isApiSlug, slug]);

  useEffect(() => {
    if (isApiSlug) return;
    if (invalidSlug) {
      setPage(null);
      setLoaded(true);
      return undefined;
    }
    let cancelled = false;
    setLoaded(false);
    getPage(slug)
      .then((data) => {
        if (!cancelled) setPage(data);
      })
      .catch(() => {
        if (!cancelled) setPage(null);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, invalidSlug, isApiSlug]);

  usePrerenderReady(invalidSlug || isApiSlug || loaded);

  if (isApiSlug) return null;

  const titleSuffix =
    siteCtx.organization_name && String(siteCtx.organization_name).trim()
      ? ` | ${String(siteCtx.organization_name).trim()}`
      : " | Gem Fashion Studios";

  if (invalidSlug) return <NotFound />;
  if (!loaded) return null;

  const titleBase = page?.title ? String(page.title).trim() : slug || "Page";
  const metaTitle = `${titleBase}${titleSuffix}`;
  const metaDesc = page?.meta_description
    ? truncateMetaDescription(String(page.meta_description).trim())
    : truncateMetaDescription(`Browse Gem Fashion Studios — ${titleBase}`);

  const webPageLd =
    slug && page
      ? {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: titleBase,
          description: metaDesc,
          url: absoluteCanonicalUrl(`/${encodeURIComponent(slug)}`),
        }
      : null;

  if (!page || !Array.isArray(page.content)) return <NotFound />;

  return (
    <>
      <Seo
        title={metaTitle}
        description={metaDesc}
        canonicalPath={`/${slug}`}
        jsonLd={webPageLd ? [webPageLd] : null}
      />
      <ContextHeroSlider context="page" contextKey={slug} />
      {page.content.length > 0 ? <BlockRenderer content={page.content} /> : null}
    </>
  );
}
